// Preis-Rollout S4: Verlauf der Preisänderungen und Verkauf seit der letzten Änderung — eine Zeile je Rollout
// (Paar Basis + eBay = eine Zeile), aufklappbar zur Liste. Nur Anzeige: Daten aus GET /pricing/rollout/verlauf/.
import { useId, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { ChevronDown } from 'lucide-react'
import {
  getRolloutVerlauf,
  type Kennung, type RolloutVerlauf as Verlauf, type VerkaufsKanal, type VerlaufAenderung, type VerlaufArt, type VerlaufAusloeser,
  type VerlaufVerkauf,
} from '@/api/priceRollout'
import { fmtEUR } from '@/lib/pricing-format'
import { portalText } from '@/lib/rollout-klartext'
import { cn } from '@/lib/utils'

const IDS_JE_ABRUF = 50

/** Eine Abfrage für alle Rollouts einer Ansicht (je 50 ids ein Abruf); ids sortiert = stabiler Cache-Schlüssel. */
export function useRolloutVerlauf(ids: readonly number[]) {
  const sortiert = [...new Set(ids)].sort((a, b) => a - b)
  return useQuery({
    queryKey: ['rollout-verlauf', sortiert],
    queryFn: async () => {
      const teile: number[][] = []
      for (let i = 0; i < sortiert.length; i += IDS_JE_ABRUF) teile.push(sortiert.slice(i, i + IDS_JE_ABRUF))
      return Object.assign({}, ...(await Promise.all(teile.map((t) => getRolloutVerlauf(t))))) as Awaited<ReturnType<typeof getRolloutVerlauf>>
    },
    enabled: sortiert.length > 0,
    staleTime: 60_000,
  })
}

const AUSLOESER: Record<VerlaufAusloeser, string> = { start: 'Start', tageslauf: 'Tageslauf', manuell: 'manuell', unbekannt: '—' }
const ART: Record<VerlaufArt, string> = { start: 'Start', schritt: 'Schritt', ziel: 'Ziel erreicht', nachgetragen: 'nachgetragen' }
const KANAL: Record<VerkaufsKanal, string> = {
  amazon: 'Amazon', ebay: 'eBay', shopify: 'Shopify', metro: 'Metro', kaufland: 'Kaufland', sonst: 'Sonstige',
}

const fmtZeit = (iso: string) =>
  new Date(iso).toLocaleString('de-DE', { timeZone: 'Europe/Berlin', day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })
const fmtZahl = (n: number) => n.toLocaleString('de-DE', { maximumFractionDigits: 3 })

export function ausloeserText(a: VerlaufAusloeser): string { return AUSLOESER[a] }

export function zeitraumText(tage: number): string {
  if (tage <= 0) return 'seit heute'
  return tage === 1 ? 'in 1 Tag' : `in ${tage} Tagen`
}

function Ausloeser({ a }: { a: VerlaufAusloeser }) {
  return a === 'unbekannt'
    ? <span className="text-muted" title="Auslöser nicht protokolliert">—</span>
    : <span>{AUSLOESER[a]}</span>
}

function Preis({ x }: { x: Pick<VerlaufAenderung, 'alt' | 'neu'> }) {
  const pfeil = x.alt === null ? '→' : x.neu > x.alt ? '↑' : '↓'
  return (
    <span className="font-mono whitespace-nowrap">
      <span className="text-ink-2">{x.alt === null ? '—' : fmtEUR(x.alt)}</span>
      <span aria-hidden className="text-muted"> {pfeil} </span>
      <span className="font-semibold text-ink">{fmtEUR(x.neu)}</span>
    </span>
  )
}

/** Preistreppe der gezeigten Änderungen (alt → neu, älteste links). Nur ab zwei Schritten. */
export function Preistreppe({ aenderungen }: { aenderungen: readonly VerlaufAenderung[] }) {
  if (aenderungen.length < 2) return null
  const folge = [...aenderungen].reverse()
  const werte = [folge[0]!.alt ?? folge[0]!.neu, ...folge.map((a) => a.neu)]
  const min = Math.min(...werte)
  const max = Math.max(...werte)
  const B = 56, H = 16
  const y = (v: number) => (max === min ? H / 2 : H - 1 - ((v - min) / (max - min)) * (H - 2))
  const dx = B / (werte.length - 1)
  let d = `M0 ${y(werte[0]!)}`
  werte.slice(1).forEach((v, i) => { d += ` H${(i + 1) * dx} V${y(v)}` })
  const titel = `${fmtEUR(werte[0])} → ${fmtEUR(werte[werte.length - 1])} in ${aenderungen.length} Schritten`
  return (
    <svg data-testid="preistreppe" width={B + 3} height={H} viewBox={`-1 0 ${B + 3} ${H}`} role="img" aria-label={titel} className="shrink-0">
      <title>{titel}</title>
      <path d={d} fill="none" stroke="currentColor" strokeWidth={1.5} className="text-primary" strokeLinejoin="round" />
      <circle cx={B} cy={y(werte[werte.length - 1]!)} r={2} className="fill-primary" />
    </svg>
  )
}

const liste = (k: readonly VerkaufsKanal[]) => k.map((x) => KANAL[x]).join(', ')

/** Welche Kanäle die Kopfzahl zählt — Basis ehrlich als „alle Kanäle außer Amazon“, ohne Kanäle mit eigenem Rollout. */
export function kanalBeschriftung(v: Pick<VerlaufVerkauf, 'kanal_filter' | 'kanal_eigen'>, istBasis: boolean): string {
  if (!istBasis) return liste(v.kanal_filter)
  return v.kanal_eigen.length > 0 ? `alle Kanäle außer Amazon, ohne ${liste(v.kanal_eigen)}` : 'alle Kanäle außer Amazon'
}

function kanalTitel(k: VerkaufsKanal, auftraege: number, v: VerlaufVerkauf, istBasis: boolean): string {
  if (v.kanal_filter.includes(k)) return `${auftraege} Aufträge – zählt`
  if (v.kanal_eigen.includes(k)) return 'Eigener Rollout – zählt nicht zur Basis'
  if (istBasis && k === 'amazon') return 'Amazon hat einen eigenen Preis – zählt nicht zur Basis'
  return 'Anderer Preis – zählt nicht zu diesem Rollout'
}

function Verkauf({ v, kennung, istBasis }: { v: VerlaufVerkauf; kennung?: Kennung | null; istBasis: boolean }) {
  const menge = kennung?.set.art === 'menge' && kennung.set.menge > 1 ? kennung.set.menge : null
  const filter = new Set(v.kanal_filter)
  const abStart = v.ab === 'start'
  return (
    <span data-testid="verlauf-verkauf" className="inline-flex flex-wrap items-baseline gap-x-1.5">
      <span className="text-muted" title={abStart ? 'Noch keine Preisänderung: gezählt ab dem ersten Lauf des Rollouts' : 'Gezählt ab der letzten Preisänderung'}>
        {abStart ? 'seit Start' : 'seither'}
      </span>
      <span className="font-mono font-semibold text-ink">{fmtZahl(v.stueck)} St.</span>
      {menge !== null && v.stueck !== 0 && (
        <span className="text-ink-2" title={`Angebot ×${menge}: ${fmtZahl(v.stueck * menge)} Teile des Grundartikels`}>
          (= {fmtZahl(v.stueck * menge)} Teile)
        </span>
      )}
      <span className="text-ink-2">· {v.auftraege} Auftr. {zeitraumText(v.tage)}</span>
      <span data-testid="verlauf-kanalfilter" className="text-muted">({kanalBeschriftung(v, istBasis)})</span>
      {v.tage > 0 && (
        <span className="text-muted" title={abStart ? 'Gleich lange Spanne direkt vor dem Start' : 'Gleich lange Spanne direkt vor der letzten Änderung'}>
          · davor {fmtZahl(v.davor.stueck)} St. · {v.davor.auftraege} Auftr.
        </span>
      )}
      {v.kanaele.length > 0 && (
        <span className="inline-flex flex-wrap gap-1" aria-label="Aufteilung je Kanal">
          {v.kanaele.map((k) => {
            const zaehlt = filter.has(k.kanal)
            return (
              <span
                key={k.kanal}
                className={cn('rounded px-1 py-px text-[10px] leading-tight', zaehlt ? 'bg-panel-2 text-ink-2' : 'border border-dashed border-line text-muted')}
                title={kanalTitel(k.kanal, k.auftraege, v, istBasis)}
              >
                {KANAL[k.kanal]} {fmtZahl(k.stueck)}
              </span>
            )
          })}
        </span>
      )}
    </span>
  )
}

/**
 * Kurzzeile „Letzte Änderung … — seither …“ und aufklappbare Änderungsliste.
 * `titel` benennt das Portal, wenn eine Zeile mehrere Rollouts zeigt (Grundartikel).
 */
export function RolloutVerlauf({ verlauf, laedt = false, fehler = false, kennung, titel }: {
  verlauf: Verlauf | undefined
  laedt?: boolean
  fehler?: boolean
  kennung?: Kennung | null
  titel?: string
}) {
  const [offen, setOffen] = useState(false)
  const listeId = useId()
  const kopf = titel ? <span className="w-20 shrink-0 text-[10px] font-semibold uppercase tracking-wider text-muted">{titel}</span> : null

  if (!verlauf) {
    return (
      <div data-testid="rollout-verlauf" className="flex items-baseline gap-2 text-[11px] text-muted">
        {kopf}
        {laedt ? 'Verlauf wird geladen…' : fehler ? 'Verlauf nicht geladen' : 'Noch keine Preisänderung'}
      </div>
    )
  }
  const letzte = verlauf.aenderungen[0]
  const mitPortal = verlauf.portale.length > 1
  const istBasis = verlauf.portale.includes('base')
  const verkauf = verlauf.verkauf ? <Verkauf v={verlauf.verkauf} kennung={kennung} istBasis={istBasis} /> : null
  return (
    <div data-testid="rollout-verlauf" className="flex items-start gap-2 text-[11px] leading-snug">
      {kopf && <span className="pt-px">{kopf}</span>}
      <div className="min-w-0 flex-1">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        {!letzte ? (
          <>
            <span className="text-muted">Noch keine Preisänderung</span>
            {verkauf}
          </>
        ) : (
          <>
            <Preistreppe aenderungen={verlauf.aenderungen} />
            <span className="inline-flex flex-wrap items-baseline gap-x-1.5" data-testid="verlauf-letzte">
              <span className="text-muted">Letzte Änderung</span>
              <span className="font-mono text-ink">{fmtZeit(letzte.at)}</span>
              {mitPortal && <span className="text-ink-2">{portalText(letzte.portal)}</span>}
              <Preis x={letzte} />
              <span className="text-ink-2">· <Ausloeser a={letzte.ausloeser} /></span>
            </span>
            {verkauf}
            <button
              type="button"
              aria-expanded={offen}
              aria-controls={listeId}
              onClick={() => setOffen((o) => !o)}
              className="inline-flex items-center gap-0.5 rounded px-1 text-primary hover:underline focus-visible:outline-2 focus-visible:outline-primary"
            >
              Verlauf ({verlauf.anzahl})
              <ChevronDown aria-hidden className={cn('size-3 transition-transform motion-reduce:transition-none', offen && 'rotate-180')} />
            </button>
          </>
        )}
      </div>
      {offen && letzte && (
        <ol id={listeId} aria-label="Preisänderungen, neueste zuerst" className="mt-1.5 max-w-[640px] divide-y divide-line-2 rounded-md border border-line-2 bg-panel">
          {verlauf.aenderungen.map((a) => (
            <li key={a.id} className="flex flex-wrap items-baseline gap-x-3 px-2 py-1 sm:grid sm:grid-cols-[92px_64px_minmax(0,1fr)_96px_80px]">
              <span className="font-mono text-ink-2">{fmtZeit(a.at)}</span>
              <span className="hidden text-ink-2 sm:inline">{mitPortal ? portalText(a.portal) : ''}</span>
              <Preis x={a} />
              <span className="hidden text-ink-2 sm:inline">{ART[a.art]}</span>
              <span className="text-ink-2"><Ausloeser a={a.ausloeser} /></span>
            </li>
          ))}
          {verlauf.anzahl > verlauf.aenderungen.length && (
            <li className="px-2 py-1 text-muted">… {verlauf.anzahl - verlauf.aenderungen.length} ältere Änderungen</li>
          )}
        </ol>
      )}
      </div>
    </div>
  )
}
